import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import Banner from "src/components/molecules/banner/Banner";
import ButtonAddNew from "src/components/atoms/button-add-new/button-add-new";
import AddOutgoingNumber from "../add-outgoing-number/add-outgoing-number";
import BusinessList from "../business-list/business-list";
import SCLabel from "src/components/atoms/sc-label/sc-label";
import { BusinessNumberViewModel } from "./number-viewmodel";
import { useAuth } from "../auth/auth-context";
import {
	IBusinessListModel,
	IFileInfo,
	INewNumberModel,
	ServiceStatus,
} from "src/types/models/apiModels";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import StatusBadge from "src/components/molecules/status-badge/status-badge";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import { BusinessViewModel } from "../business-list/business-list.view-model";
import Alert from "src/components/atoms/alert/alert";
import PhoneNumberDisplay from "src/components/atoms/phone-number-display/phone-number-display";
import { ReactComponent as DeleteIcon } from "../../../assets/delete2.svg";
import { CopyIcon } from "lucide-react";
import { ClipboardService } from "src/helpers/clipboard-service";
import ActionPanel from "../action-panel/action-panel";

export interface IBusinessNumbersProps {
	editNumbers?: INewNumberModel[];
	onChange?: (numbers: INewNumberModel[]) => void;
}

const Numbers: React.FC<IBusinessNumbersProps> = ({ editNumbers }) => {
	const { user } = useAuth();
	const viewModel = useRef<BusinessNumberViewModel>();
	const [loading, setLoading] = useState(true);
	const [loadingMessage, setLoadingMessage] = useState(
		"Loading your numbers"
	);
	const [business, setBusiness] = useState<IBusinessListModel | undefined>(
		undefined
	);
	const [hasMoreThanOneBusiness, setHasMoreThanOneBusiness] = useState(false);
	const [businessId, setBusinessId] = useState<string>("");
	const [selectBusinessDialogOpen, setSelectBusinessDialogOpen] =
		useState<boolean>(false);
	const [businessNumbers, setBusinessNumbers] = useState<INewNumberModel[]>(
		editNumbers ?? []
	);
	const [addNumberDialogOpen, setAddNumberDialogOpen] =
		useState<boolean>(false);
	const [numberAddError, setNumberAddError] = useState("");
	const [numberDeleteError, setDeleteAddError] = useState("");
	const [showNumberAddingError, setShowNumberAddingError] = useState(false);
	const [
		showNumberDeleteConfirmationBox,
		setShowNumberDeleteConfirmationBox,
	] = useState(false);
	const [numberToDelete, setNumberToDelete] = useState("");
	const [showNumberDeleteError, setShowNumberDeleteError] = useState(false);
	const businessListViewModel = useRef<BusinessViewModel | null>(null);
	const [canAddNewNumberToBusiness, setCanAddNewNumberToBusiness] =
		useState(false);

	useEffect(() => {
		const isBusinessApproved =
			business?.status === "Approved" || business?.status === "Active";

		const userPermissions = user?.permissionBag.permissions;

		if (
			isBusinessApproved &&
			userPermissions &&
			userPermissions.filter(
				(p) => p.name === "Number" && p.value === "Create"
			).length > 0
		) {
			setCanAddNewNumberToBusiness(true);
		} else {
			setCanAddNewNumberToBusiness(false);
		}
	}, [business]);

	useEffect(() => {
		const canAddNewNumberToBusiness = async () => {
			const isBusinessApproved =
				business?.status === "Approved" ||
				business?.status === "Active";

			const userPermissions = user?.permissionBag.permissions;

			if (
				isBusinessApproved &&
				userPermissions &&
				userPermissions.filter(
					(p) => p.name === "Number" && p.value === "Create"
				).length > 0
			) {
				setCanAddNewNumberToBusiness(true);
			} else {
				setCanAddNewNumberToBusiness(false);
			}
		};

		canAddNewNumberToBusiness();

		if (user && user.accessToken) {
			viewModel.current = new BusinessNumberViewModel(user?.accessToken);

			const fetchData = async () => {
				try {
					if (user && user.accessToken) {
						setLoading(true);
						businessListViewModel.current = new BusinessViewModel(
							user.accessToken
						);
						const response =
							await businessListViewModel.current?.fetchListAsync();
						setHasMoreThanOneBusiness(response.length > 1);
						setBusiness(() => response[0]);
						setBusinessId(response[0].businessId);
						setLoading(false);
					}
				} catch (error) {
					console.error("Error fetching data:", error);
				} finally {
				}
			};

			fetchData();
		}
	}, []);

	useEffect(() => {
		fetchData(); // Call the async function inside useEffect
	}, [businessId]); // Dependency array ensures this runs when businessId changes

	const fetchData = async () => {
		if (businessId && viewModel.current) {
			setLoading(true);
			setNumberAddError("");
			setShowNumberAddingError(false);
			// console.log(`getting number for businessId ${businessId}`);
			try {
				const data = await viewModel.current.getNumbers(businessId);
				if (data) {
					setBusinessNumbers(data);
					setLoading(false);
				}
			} catch (error) {
				setBusinessNumbers([]);
				setLoading(false);
			}
		}
	};

	const toggleBusinessDialog = () => {
		setSelectBusinessDialogOpen((prevValue) => !prevValue);
	};

	const toggleDialog = () => {
		setAddNumberDialogOpen((prevValue) => !prevValue);
	};

	function handleOnSelectBusiness(business: IBusinessListModel): void {
		setBusiness(business);
		setBusinessId(business.businessId);
		toggleBusinessDialog();
	}

	function showBusinessList(): void {
		toggleBusinessDialog();
	}

	function handleOnAddBusiness(): void {
		throw new Error("Function not implemented.");
	}

	const addNumber = (number: string, fileInfo: IFileInfo) => {
		console.log("In handleAddNumberCallback");
		setShowNumberAddingError(false);
		setNumberAddError("");

		setBusinessNumbers((prevNumbers) => {
			// Check if the number already exists in the list
			const numberExists = prevNumbers.some(
				(num) => num.number === number
			);

			if (numberExists) {
				// console.log("Number already exists. Update rejected.");
				setShowNumberAddingError(true);
				setNumberAddError("Number already exists. Update rejected.");
				// You can add more logic here, like showing an error message
				return prevNumbers; // Return the existing numbers without adding
			}

			const updatedNumbers = [
				...prevNumbers,
				{
					number: number,
					ownershipDocuments: [fileInfo],
					status: "Added" as ServiceStatus,
				},
			];

			return updatedNumbers;
		});

		setAddNumberDialogOpen(false);
	};

	const handleAddNewNumberClick = () => {
		setNumberAddError("");
		setShowNumberAddingError(false);
		setAddNumberDialogOpen(true);
	};

	const handleDeleteBusinessNumber = async (
		businessNumber: string
	): Promise<void> => {
		if (business && viewModel.current) {
			setLoading(true);
			setLoadingMessage("Deleting number...");
			const result = await viewModel.current.deleteBusinessNumberAsync(
				business.businessId,
				[businessNumber]
			);
			setLoading(false);
			if (result.result) {
				fetchData();
			} else {
				if (result.error) {
					setDeleteAddError(result.error);
					setShowNumberDeleteError(true);
				}
			}
		}
	};

	const handleSubmit = async () => {
		// Filter the business numbers with status "New"
		const newBusinessNumbers = businessNumbers.filter(
			(number) => number.status === "Added"
		);

		// Ensure that business and businessId exist
		if (business && business.businessId) {
			setLoading(true);
			setLoadingMessage("Adding number...");
			try {
				const result = await viewModel.current?.submitBusinessNumbers(
					business.businessId,
					newBusinessNumbers
				);

				if (result && result.result) {
					const failedNumbers = result.result.businessNumbers.filter(
						(number) => number.success === false
					);

					if (failedNumbers.length > 0) {
						let errorString = "";
						failedNumbers.map((failedNumber) => {
							errorString =
								errorString +
								" " +
								failedNumber.businessNumber +
								" -> " +
								failedNumber.message +
								"</br>";
						});

						setNumberAddError(errorString);
						setShowNumberAddingError(true);
					}
				}
				if (result) {
					const data =
						await viewModel.current?.getNumbers(businessId);
					if (data) {
						setBusinessNumbers(data);
					}
				}
				setLoading(false);
			} catch (error) {
				setLoading(false);
			}
		}
	};

	const handleRemoveNumber = async (number: string) => {
		const updatedItems = businessNumbers.filter(
			(item) => item.number !== number
		);

		setBusinessNumbers(updatedItems);
	};

	return (
		<>
			<Banner label="Business Numbers">
				<>
					<div className="bg-white p-6 rounded-lg drop-shadow-2xl">
						<div className="flex items-center justify-start w-full p-2">
							<span className="font-Poppins font-semibold text-md uppercase tracking-wide">
								Select Business
							</span>
						</div>

						<SCLabel color="#000">
							{business ? (
								<span
									onClick={
										hasMoreThanOneBusiness
											? showBusinessList
											: undefined
									}
									className="flex bg-gradient-to-r from-sky-900 to-sky-700 p-3 drop-shadow-md"
								>
									{business.logo && (
										<div className="flex items-center justify-start gap-4 flex-shrink-0">
											{business.logo.signedUrl && (
												<img
													className="w-20 h-20 rounded-full flex-shrink-0"
													src={
														business.logo.signedUrl
													}
													alt="Business Logo"
												/>
											)}
											<div className="flex flex-col items-start justify-center gap-2">
												<div className="w-full flex items-center justify-start gap-2 text-white">
													<span className="font-Poppins text-2xl font-semibold tracking-wide">
														{business.name}
													</span>
												</div>

												<StatusBadge
													status={business.status}
												></StatusBadge>
											</div>
										</div>
									)}
								</span>
							) : (
								<>
									<div className="w-full flex">
										<HyperLink
											href={"#"}
											label={"Select a business"}
											onClick={showBusinessList}
										></HyperLink>
									</div>
								</>
							)}
						</SCLabel>

						<div className="font-Poppins overflow-x-auto w-full mt-4 drop-shadow-md">
							<table className="min-w-[650px]  w-full border-collapse border border-gray-300">
								{/* Table Header */}
								<thead className="bg-gradient-to-r from-sky-900 to-sky-700 text-white">
									<tr>
										<th className="font-Poppins border border-gray-300 px-4 py-2 text-center text-md tracking-wider font-semibold">
											OUTGOING NO.
										</th>
										<th className="font-Poppins border border-gray-300 px-4 py-2 text-center text-md tracking-wider font-semibold">
											PROOF OF OWNERSHIP
										</th>
										<th className="font-Poppins border border-gray-300 px-4 py-2 text-center text-md tracking-wider font-semibold">
											STATUS
										</th>
										<th className="font-Poppins border border-gray-300 px-4 py-2 text-center text-md tracking-wider font-semibold">
											ACTION
										</th>
									</tr>
								</thead>
								{/* Table Body */}
								<tbody>
									{businessNumbers.map((row, index) => (
										<tr
											key={index}
											className="hover:bg-gray-50"
										>
											{/* Outgoing Number */}
											<td className="border border-gray-300 px-4 py-2 text-center text-black">
												<div className="flex items-center justify-center gap-4">
													<span className="font-Poppins tracking-wide text-black text-lg font-semibold">
														<PhoneNumberDisplay
															number={row.number}
															hasPlus={true}
														></PhoneNumberDisplay>
													</span>
													<button
														className="cursor-copy"
														onClick={() => {
															ClipboardService.copyToClipboard(
																row.number
															);
														}}
													>
														<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
													</button>
												</div>
											</td>
											{/* Proof of Ownership */}
											<td className="border border-gray-300 px-4 py-2 text-center">
												<div className="flex justify-center gap-2 items-center">
													<button
														className="text-blue-600 hover:text-blue-800 focus:outline-none"
														aria-label="Download Proof"
														onClick={() => {
															const url =
																row
																	.ownershipDocuments[0]
																	.signedUrl;
															if (url) {
																const anchor =
																	document.createElement(
																		"a"
																	);
																anchor.href =
																	url;
																anchor.target =
																	"_blank";
																anchor.download =
																	""; // Optional: Add a specific filename if needed
																document.body.appendChild(
																	anchor
																);
																anchor.click();
																document.body.removeChild(
																	anchor
																);
															} else {
																console.error(
																	"URL is not available."
																);
															}
														}}
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															fill="currentColor"
															className="size-6 text-gray-800"
														>
															<path d="M12 1.5a.75.75 0 0 1 .75.75V7.5h-1.5V2.25A.75.75 0 0 1 12 1.5ZM11.25 7.5v5.69l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V7.5h3.75a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9a3 3 0 0 1 3-3h3.75Z" />
														</svg>
													</button>
												</div>
											</td>
											{/* Status */}
											<td className="border border-gray-300 px-4 py-2 text-center">
												<StatusBadge
													status={row.status}
												></StatusBadge>
											</td>
											{/* Action */}
											<td className="border border-gray-300 px-4 py-2 text-center">
												{row.status === "Added" ? (
													<button
														className="text-red-600 hover:text-red-800 focus:outline-none"
														onClick={() =>
															handleRemoveNumber(
																row.number
															)
														}
														aria-label="Remove Number"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															className="w-5 h-5"
															fill="none"
															viewBox="0 0 24 24"
															stroke="currentColor"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth="2"
																d="M6 18L18 6M6 6l12 12"
															/>
														</svg>
													</button>
												) : row.status === "New" ||
												  row.status === "Approved" ? (
													<div className="w-full flex items-center justify-center">
														<button
															onClick={() => {
																setNumberToDelete(
																	row.number
																);
																setShowNumberDeleteConfirmationBox(
																	true
																);
															}}
														>
															<DeleteIcon
																className={`size-6 text-red-600`}
															></DeleteIcon>
														</button>
													</div>
												) : (
													// Default fallback for other statuses
													<span>-</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className="flex items-center justify-end w-full gap-4 py-4">
							<ButtonAddNew
								disabled
								text="Import multiple"
								onClick={handleAddNewNumberClick}
							></ButtonAddNew>
							<ButtonAddNew
								disabled={!canAddNewNumberToBusiness}
								onClick={handleAddNewNumberClick}
							></ButtonAddNew>
						</div>
						<ButtonPro
							label={"Submit"}
							onClick={handleSubmit}
							disabled={
								!business ||
								businessNumbers.filter(
									(number) => number.status === "Added"
								).length === 0
							}
						></ButtonPro>
					</div>
				</>
			</Banner>
			{/* loader */}
			<LoadingModal
				open={loading}
				message={loadingMessage}
			></LoadingModal>
			{/* Add number popup */}

			{/* Delete number confirmation */}
			<Dialog
				open={showNumberDeleteConfirmationBox}
				onClose={() => setShowNumberDeleteConfirmationBox(false)}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						display: "flex", // Align content properly
						justifyContent: "center", // Center horizontally
						alignItems: "center", // Center vertically
						overflow: "hidden", // Prevent horizontal overflow
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label="Are you sure you want to delete this number?">
						<div className="flex flex-col items-center justify-center gap-4">
							<PhoneNumberDisplay
								number={numberToDelete}
								hasPlus={true}
							></PhoneNumberDisplay>
							<ActionPanel
								primaryButtonLabel={"Yes"}
								primaryButtonEnabled={true}
								secondaryButtonLabel={"No"}
								secondaryButtonEnabled={true}
								primaryButtonAction={async () => {
									await handleDeleteBusinessNumber(
										numberToDelete
									);
									setShowNumberDeleteConfirmationBox(false);
								}}
								secondaryButtonAction={function (): void {
									setNumberToDelete("");
									setShowNumberDeleteConfirmationBox(false);
								}}
							></ActionPanel>
						</div>
					</Banner>
				</DialogContent>
			</Dialog>

			<Dialog
				open={showNumberAddingError}
				onClose={() => setShowNumberAddingError(false)}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						display: "flex", // Align content properly
						justifyContent: "center", // Center horizontally
						alignItems: "center", // Center vertically
						overflow: "hidden", // Prevent horizontal overflow
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label="Delete error">
						<Alert
							text={numberAddError}
							type={"error"}
							isHTML
						></Alert>
						<ButtonPro
							label={"Close"}
							onClick={() => setShowNumberAddingError(false)}
						></ButtonPro>
					</Banner>
				</DialogContent>
			</Dialog>

			<Dialog
				open={showNumberDeleteError}
				onClose={() => setShowNumberDeleteError(false)}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						display: "flex", // Align content properly
						justifyContent: "center", // Center horizontally
						alignItems: "center", // Center vertically
						overflow: "hidden", // Prevent horizontal overflow
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label="Delete error">
						<Alert
							text={numberDeleteError}
							type={"error"}
							isHTML
						></Alert>
						<ButtonPro
							label={"Close"}
							onClick={() => setShowNumberDeleteError(false)}
						></ButtonPro>
					</Banner>
				</DialogContent>
			</Dialog>

			<Dialog
				open={addNumberDialogOpen}
				onClose={toggleDialog}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						display: "flex", // Align content properly
						justifyContent: "center", // Center horizontally
						alignItems: "center", // Center vertically
						overflow: "hidden", // Prevent horizontal overflow
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<AddOutgoingNumber onAdd={addNumber} />
				</DialogContent>
			</Dialog>

			{/* Select business pop-up */}
			<Dialog
				open={selectBusinessDialogOpen}
				onClose={toggleBusinessDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						display: "flex", // Align content properly
						justifyContent: "center", // Center horizontally
						alignItems: "center", // Center vertically
						overflow: "hidden", // Prevent horizontal overflow
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label="Select Business">
						<BusinessList
							onAddNewClick={handleOnAddBusiness}
							onSelect={handleOnSelectBusiness}
							selectMode={true}
							onClose={toggleBusinessDialog}
						></BusinessList>
					</Banner>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default Numbers;
